import { FC, ReactChildren, useCallback, useMemo, useState } from 'react';
import { LangBlockWrapper, LangContainer, LangItem, LangTitle, LangTitleWrapper, StyledInput, StyledTextarea, Title } from './styles';
import { CommonInput, CommonLabel, FlexBox, useBreakpoint } from '@brg/ui';
import { useTheme } from 'styled-components';
import { WordCount } from 'shared/pages/designerEditView/styles';
import { ABOUT_MAX_LENGTH } from 'shared/pages/designerEditView/support/const';
import { AppTester } from 'supporting';
import { InfoIcon } from '../InfoIcon/InfoIcon';

export enum InputType {
    textarea = 'textarea',
    text = 'text'
}

interface LangComponentProps {
 onLangChange?: () => void;
 lang?: string;
 orientation?: 'row' | 'column';
 isCompetitionEdit?: boolean;
 isGeograffee?: boolean;
}

interface TProps extends LangComponentProps {
 title: string;
 mainTitle: boolean;
 required?: boolean;
 langVisible?: boolean;
 children?: ReactChildren;
 placeholder?: string,
 onChange?: (i: any, lang?: string) => void;
 value?: string;
 alignItems?: 'center' | 'flex-start' | 'flex-end';
 hasHint?: boolean;
 hintMgs?: any;
}

interface ILangBlock extends LangComponentProps {
    title: string;
    mainTitle: boolean;
    value: Record<string, string> | string;
    required?: boolean;
    langVisible?: boolean;
    children?: ReactChildren;
    placeholder?: string,
    onChange?: (i: any, lang?: string) => void;
    onFocus?: (i: any, lang?: string) => void;
    onBlur?: (i: any, lang?: string) => void;
    inputType?: InputType;
    hasHint?: boolean;
    hintMgs?: any;
    maxLength?: number;
}

export const LangComponent: FC<LangComponentProps> = ({orientation, onLangChange = () => {}, lang = 'ru', isCompetitionEdit, isGeograffee }) => {  
    return (
        <LangContainer onClick={onLangChange} orientation={orientation} isGeograffee={isGeograffee}>
            <LangItem className={lang === 'ru' ? 'active' : ''} lang={lang} active={lang === 'ru'} isCompetitionEdit={isCompetitionEdit} isGeograffee={isGeograffee}>RU</LangItem>
            <LangItem className={lang === 'en' ? 'active' : ''} lang={lang} active={lang === 'en'} isCompetitionEdit={isCompetitionEdit} isGeograffee={isGeograffee}>EN</LangItem>
        </LangContainer>
    )
}

export const LangTitleBox: FC<TProps> = ({ title, mainTitle, onLangChange, lang = 'ru', required, orientation = 'column', langVisible = true, isCompetitionEdit, alignItems, hasHint, hintMgs  }) => {
    const is375 = useBreakpoint(375);

    const Lang = useMemo(() => {
        if (onLangChange && langVisible) {
            return (
                <LangComponent orientation={orientation} onLangChange={onLangChange} lang={lang} isCompetitionEdit={isCompetitionEdit} />
            )
        }
    }, [onLangChange, is375, orientation, langVisible, isCompetitionEdit])

    return (
        <LangTitleWrapper className={'lang-container'} mainTitle={mainTitle} orientation={orientation} required={required} alignItems={alignItems} >
            <Title mainTitle={mainTitle} orientation={orientation}>
                {title}
                {hasHint && <InfoIcon msg={hintMgs} />}
            </Title>
            {Lang}
        </LangTitleWrapper>
    )
}

export const LangTitleBlock: FC<ILangBlock> = (
    { 
        title, 
        mainTitle,  
        required,
        inputType, 
        orientation = 'column', 
        langVisible = true,
        isCompetitionEdit,
        placeholder,
        onChange,
        onBlur,
        onFocus,
        value,
        hasHint,
        hintMgs,
        maxLength
    }) => {
    const theme = useTheme();
    const [currentLang, setCurrentLang] = useState('ru');
    const isGeograffee = AppTester.isGeograffee();

    const currentValue: string = useMemo(() => {
        if (Array.isArray(value)) {
            return value?.find((i) => i?.lang === currentLang)?.name || ''
        }
        if (typeof value === 'string' ) {
            return value;
        }
        return value?.[currentLang]
    }, [currentLang, value])

    const onLanguageChange = useCallback(() => setCurrentLang(currentLang === 'ru' ? 'en' : 'ru'), [currentLang])

    const lang = useMemo(() => {
            return (
                <FlexBox className='lang-label-block'>
                    <LangTitle 
                        className={'lang-label'}
                        langVisible={langVisible} 
                        mainTitle={mainTitle} 
                        orientation={orientation}
                    >
                        {title}
                        {hasHint && <InfoIcon stc msg={hintMgs} />}
                        
                    </LangTitle>
                    {langVisible && 
                        <LangComponent 
                            orientation={orientation} 
                            onLangChange={onLanguageChange} 
                            lang={currentLang}
                            isCompetitionEdit={isCompetitionEdit}
                            isGeograffee={isGeograffee}
                        />
                    }
                </FlexBox>
            )
    }, [onLanguageChange, orientation, langVisible, currentLang, mainTitle, title, isCompetitionEdit  ])

    const input = useMemo(() => {
        if (onChange) {
            return (
                <FlexBox direction='column' directionMobile='column' width={'100%'} className='lang-input-block' style={{ position: 'relative' }}>
                    {inputType === InputType.textarea ? (
                        <StyledTextarea
                            style={{
                                backgroundColor: theme.designerEdit.aboutProfile.bg,
                                color: theme.designerEdit.inputs.color,
                                resize: 'vertical',
                            }}
                            placeholder={placeholder}
                            onChange={(ev) => onChange(ev, currentLang)}
                            value={currentValue}
                        />
                    ) : (
                        <StyledInput
                            className={'styled-input'} 
                            onChange={(ev) => onChange(ev, currentLang)}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            value={currentValue}
                            placeholder={placeholder}
                            style={{
                                color: theme.designerEdit.inputs.color,
                            }}
                        />
                    )}
                    {inputType === InputType.textarea && <WordCount>{(currentValue?.length || '0') + '/' + maxLength || ABOUT_MAX_LENGTH}</WordCount>}
                </FlexBox>
            )
        }
    }, [onChange, placeholder, currentLang, currentValue, inputType, theme ])

    return (
        <LangBlockWrapper
            className={'lang-row-container'} 
            mainTitle={mainTitle} 
            orientation={orientation} 
            required={required} 
            inputType={inputType} 
            langVisible={langVisible} 
        >
            {lang}
            {input}
        </LangBlockWrapper>
    )
}import { FC, ReactChildren, useCallback, useMemo, useState } from 'react';
import { LangBlockWrapper, LangContainer, LangItem, LangTitle, LangTitleWrapper, StyledInput, StyledTextarea, Title } from './styles';
import { CommonInput, CommonLabel, FlexBox, useBreakpoint } from '@brg/ui';
import { useTheme } from 'styled-components';
import { WordCount } from 'shared/pages/designerEditView/styles';
import { ABOUT_MAX_LENGTH } from 'shared/pages/designerEditView/support/const';
import { AppTester } from 'supporting';
import { InfoIcon } from '../InfoIcon/InfoIcon';

export enum InputType {
    textarea = 'textarea',
    text = 'text'
}

interface LangComponentProps {
 onLangChange?: () => void;
 lang?: string;
 orientation?: 'row' | 'column';
 isCompetitionEdit?: boolean;
 isGeograffee?: boolean;
}

interface TProps extends LangComponentProps {
 title: string;
 mainTitle: boolean;
 required?: boolean;
 langVisible?: boolean;
 children?: ReactChildren;
 placeholder?: string,
 onChange?: (i: any, lang?: string) => void;
 value?: string;
 alignItems?: 'center' | 'flex-start' | 'flex-end';
 hasHint?: boolean;
 hintMgs?: any;
}

interface ILangBlock extends LangComponentProps {
    title: string;
    mainTitle: boolean;
    value: Record<string, string> | string;
    required?: boolean;
    langVisible?: boolean;
    children?: ReactChildren;
    placeholder?: string,
    onChange?: (i: any, lang?: string) => void;
    onFocus?: (i: any, lang?: string) => void;
    onBlur?: (i: any, lang?: string) => void;
    inputType?: InputType;
    hasHint?: boolean;
    hintMgs?: any;
    maxLength?: number;
}

export const LangComponent: FC<LangComponentProps> = ({orientation, onLangChange = () => {}, lang = 'ru', isCompetitionEdit, isGeograffee }) => {  
    return (
        <LangContainer onClick={onLangChange} orientation={orientation} isGeograffee={isGeograffee}>
            <LangItem className={lang === 'ru' ? 'active' : ''} lang={lang} active={lang === 'ru'} isCompetitionEdit={isCompetitionEdit} isGeograffee={isGeograffee}>RU</LangItem>
            <LangItem className={lang === 'en' ? 'active' : ''} lang={lang} active={lang === 'en'} isCompetitionEdit={isCompetitionEdit} isGeograffee={isGeograffee}>EN</LangItem>
        </LangContainer>
    )
}

export const LangTitleBox: FC<TProps> = ({ title, mainTitle, onLangChange, lang = 'ru', required, orientation = 'column', langVisible = true, isCompetitionEdit, alignItems, hasHint, hintMgs  }) => {
    const is375 = useBreakpoint(375);

    const Lang = useMemo(() => {
        if (onLangChange && langVisible) {
            return (
                <LangComponent orientation={orientation} onLangChange={onLangChange} lang={lang} isCompetitionEdit={isCompetitionEdit} />
            )
        }
    }, [onLangChange, is375, orientation, langVisible, isCompetitionEdit])

    return (
        <LangTitleWrapper className={'lang-container'} mainTitle={mainTitle} orientation={orientation} required={required} alignItems={alignItems} >
            <Title mainTitle={mainTitle} orientation={orientation}>
                {title}
                {hasHint && <InfoIcon msg={hintMgs} />}
            </Title>
            {Lang}
        </LangTitleWrapper>
    )
}

export const LangTitleBlock: FC<ILangBlock> = (
    { 
        title, 
        mainTitle,  
        required,
        inputType, 
        orientation = 'column', 
        langVisible = true,
        isCompetitionEdit,
        placeholder,
        onChange,
        onBlur,
        onFocus,
        value,
        hasHint,
        hintMgs,
        maxLength
    }) => {
    const theme = useTheme();
    const [currentLang, setCurrentLang] = useState('ru');
    const isGeograffee = AppTester.isGeograffee();

    const currentValue: string = useMemo(() => {
        if (Array.isArray(value)) {
            return value?.find((i) => i?.lang === currentLang)?.name || ''
        }
        if (typeof value === 'string' ) {
            return value;
        }
        return value?.[currentLang]
    }, [currentLang, value])

    const onLanguageChange = useCallback(() => setCurrentLang(currentLang === 'ru' ? 'en' : 'ru'), [currentLang])

    const lang = useMemo(() => {
            return (
                <FlexBox className='lang-label-block'>
                    <LangTitle 
                        className={'lang-label'}
                        langVisible={langVisible} 
                        mainTitle={mainTitle} 
                        orientation={orientation}
                    >
                        {title}
                        {hasHint && <InfoIcon stc msg={hintMgs} />}
                        
                    </LangTitle>
                    {langVisible && 
                        <LangComponent 
                            orientation={orientation} 
                            onLangChange={onLanguageChange} 
                            lang={currentLang}
                            isCompetitionEdit={isCompetitionEdit}
                            isGeograffee={isGeograffee}
                        />
                    }
                </FlexBox>
            )
    }, [onLanguageChange, orientation, langVisible, currentLang, mainTitle, title, isCompetitionEdit  ])

    const input = useMemo(() => {
        if (onChange) {
            return (
                <FlexBox direction='column' directionMobile='column' width={'100%'} className='lang-input-block' style={{ position: 'relative' }}>
                    {inputType === InputType.textarea ? (
                        <StyledTextarea
                            style={{
                                backgroundColor: theme.designerEdit.aboutProfile.bg,
                                color: theme.designerEdit.inputs.color,
                                resize: 'vertical',
                            }}
                            placeholder={placeholder}
                            onChange={(ev) => onChange(ev, currentLang)}
                            value={currentValue}
                        />
                    ) : (
                        <StyledInput
                            className={'styled-input'} 
                            onChange={(ev) => onChange(ev, currentLang)}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            value={currentValue}
                            placeholder={placeholder}
                            style={{
                                color: theme.designerEdit.inputs.color,
                            }}
                        />
                    )}
                    {inputType === InputType.textarea && <WordCount>{(currentValue?.length || '0') + '/' + maxLength || ABOUT_MAX_LENGTH}</WordCount>}
                </FlexBox>
            )
        }
    }, [onChange, placeholder, currentLang, currentValue, inputType, theme ])

    return (
        <LangBlockWrapper
            className={'lang-row-container'} 
            mainTitle={mainTitle} 
            orientation={orientation} 
            required={required} 
            inputType={inputType} 
            langVisible={langVisible} 
        >
            {lang}
            {input}
        </LangBlockWrapper>
    )
}